import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface ReactivationRequest {
  id: string;
  company_id: string;
  requested_by: string;
  status: string;
  reason: string;
  attachment_url: string | null;
  attachment_name: string | null;
  superuser_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  cooldown_expires_at: string | null;
  created_at: string;
  updated_at: string;
  company_name: string;
  requester_first_name: string;
  requester_last_name: string;
  requester_email: string;
}

export function useReactivationRequests() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["reactivation-requests"],
    staleTime: 30_000,
    queryFn: async (): Promise<ReactivationRequest[]> => {
      const { data: requests, error: reqErr } = await supabase
        .from("company_suspension_requests")
        .select(
          "id, company_id, requested_by, status, reason, attachment_url, attachment_name, superuser_notes, reviewed_by, reviewed_at, cooldown_expires_at, created_at, updated_at"
        )
        .order("created_at", { ascending: false });

      if (reqErr) throw reqErr;
      const rows = requests ?? [];
      if (rows.length === 0) return [];

      const companyIds = Array.from(new Set(rows.map((r) => r.company_id)));
      const userIds = Array.from(new Set(rows.map((r) => r.requested_by)));

      const [companiesRes, profilesRes] = await Promise.all([
        supabase.from("companies").select("id, name").in("id", companyIds),
        supabase
          .from("profiles")
          .select("id, first_name, last_name, email")
          .in("id", userIds),
      ]);

      if (companiesRes.error) throw companiesRes.error;
      if (profilesRes.error) throw profilesRes.error;

      const companyMap = new Map(
        (companiesRes.data ?? []).map((c) => [c.id, c.name])
      );
      const profileMap = new Map(
        (profilesRes.data ?? []).map((p) => [p.id, p])
      );

      return rows.map((r) => {
        const profile = profileMap.get(r.requested_by);
        return {
          ...r,
          company_name: companyMap.get(r.company_id) ?? "Unknown company",
          requester_first_name: profile?.first_name ?? "",
          requester_last_name: profile?.last_name ?? "",
          requester_email: profile?.email ?? "",
        };
      });
    },
  });

  useEffect(() => {
    const channel = supabase
      .channel("reactivation-requests")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "company_suspension_requests" },
        () => {
          queryClient.invalidateQueries({ queryKey: ["reactivation-requests"] });
          queryClient.invalidateQueries({ queryKey: ["pending-requests-count"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  return {
    requests: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error as Error | null,
    refetch: query.refetch,
  };
}
